import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DownloadIcon } from 'lucide-react'
import { useCurrentShow } from '@/context/current-show'
import { SelectShowNotice } from '@/components/current-show'
import { useShowStaff } from '@/api/hooks'
import { ReportLayout } from '@/layouts/ReportLayout'
import { Button } from '@/components/ui/button'
import { triggerReportPrint } from '@/lib/print'
import { buildStaffCertificates } from '@/lib/staff-certificates'
import certificateFrameUrl from '@/assets/cert_frame.svg?url'
import { parseDateOnlyToLocal } from '@/lib/date'

function formatShowDateRange(start?: string | null, end?: string | null): string | null {
  const formatter = new Intl.DateTimeFormat(undefined, { dateStyle: 'long' })
  const startDate = start ? parseDateOnlyToLocal(start) : null
  const endDate = end ? parseDateOnlyToLocal(end) : null

  if (startDate && endDate) {
    if (startDate.getTime() === endDate.getTime()) {
      return formatter.format(startDate)
    }
    return `${formatter.format(startDate)} – ${formatter.format(endDate)}`
  }

  if (startDate) return formatter.format(startDate)
  if (endDate) return formatter.format(endDate)
  return null
}

export default function StaffCertificatesReportPage() {
  const { currentShow } = useCurrentShow()
  const [searchParams] = useSearchParams()

  const showIdParam = searchParams.get('showId') ?? undefined
  const showNameParam = searchParams.get('showName') ?? undefined
  const showLocationParam = searchParams.get('showLocation') ?? undefined
  const showStartDateParam = searchParams.get('showStartDate') ?? undefined
  const showEndDateParam = searchParams.get('showEndDate') ?? undefined

  const showId = currentShow?.id ?? showIdParam
  const showName = currentShow?.name ?? showNameParam ?? 'Selected Show'
  const showLocation = currentShow?.location ?? showLocationParam ?? undefined
  const showStartDate = currentShow?.startDate ?? showStartDateParam ?? undefined
  const showEndDate = currentShow?.endDate ?? showEndDateParam ?? undefined

  const staffQuery = useShowStaff(showId)
  const assignments = staffQuery.data ?? []

  const certificates = useMemo(() => buildStaffCertificates(assignments), [assignments])
  const formattedDates = formatShowDateRange(showStartDate, showEndDate)

  if (!showId) {
    return <SelectShowNotice description="Choose a show to print appreciation certificates." />
  }

  const renderStatusLayout = (
    message: string,
    messageClass = 'text-base text-muted-foreground'
  ) => (
    <ReportLayout
      className="certificate-layout"
      header={
        <div className="certificate-layout__header">
          <p className="certificate-layout__label">Certificate of Appreciation</p>
          <h1 className="certificate-layout__show-name">{showName}</h1>
          {formattedDates ? <p className="certificate-layout__meta">{formattedDates}</p> : null}
          {showLocation ? <p className="certificate-layout__meta">{showLocation}</p> : null}
        </div>
      }
    >
      <p className={messageClass}>{message}</p>
    </ReportLayout>
  )

  if (staffQuery.isLoading) {
    return renderStatusLayout('Preparing certificates…')
  }

  if (staffQuery.isError) {
    return renderStatusLayout(
      staffQuery.error?.message ?? 'Unable to load staff assignments.',
      'text-base text-destructive'
    )
  }

  if (certificates.length === 0) {
    return renderStatusLayout('No staff assignments have been recorded.')
  }

  return (
    <div className="certificate-report">
      <div className="certificate-report__controls print-hidden">
        <Button type="button" variant="outline" onClick={triggerReportPrint}>
          <DownloadIcon className="mr-2 h-4 w-4" aria-hidden="true" />
          Download PDF
        </Button>
      </div>

      <div className="certificate-report__pages">
        {certificates.map(certificate => {
          const signerName = certificate.signer.name ?? ''
          const serviceRole = certificate.isPresidingJudge
            ? 'Presiding Judge'
            : certificate.recipient.roleLabel

          return (
            <div className="certificate-page" key={certificate.id}>
              <div className="certificate-page__frame" aria-hidden="true">
                <img src={certificateFrameUrl} alt="" />
              </div>
              <ReportLayout
                className="certificate-layout"
                header={
                  <div className="certificate-layout__header">
                    <p className="certificate-layout__label">Certificate of Appreciation</p>
                    <h1 className="certificate-layout__show-name">{showName}</h1>
                    {formattedDates ? (
                      <p className="certificate-layout__meta">{formattedDates}</p>
                    ) : null}
                    {showLocation ? (
                      <p className="certificate-layout__meta">{showLocation}</p>
                    ) : null}
                  </div>
                }
              >
                <section className="certificate-layout__body">
                  <p className="certificate-layout__intro">Presented to</p>
                  <p className="certificate-layout__recipient-name">{certificate.recipient.name}</p>
                  <p className="certificate-layout__role">for dedicated service as {serviceRole}</p>
                  <p className="certificate-layout__message">
                    Thank you for supporting the success of the {showName}.
                  </p>

                  <div className="certificate-layout__signature-block">
                    <div className="certificate-layout__signature-meta" role="presentation">
                      <span className="certificate-layout__signature-name">{signerName}</span>
                      <span className="certificate-layout__signature-title">
                        {certificate.signer.title}
                      </span>
                    </div>
                  </div>
                </section>
              </ReportLayout>
            </div>
          )
        })}
      </div>
    </div>
  )
}
